import { useState } from 'react';

import { Col, Row, Container, Stars, Avatar, Typography } from 'darrius';

import { shouldRenderComponent } from '../../../utils/shouldRenderComponent';
import { INSTANT_MATCH_V1_IS_ENABLED } from '../../../utils/featureFlags/instantMatchV1';

import { CIMessageDialog, Dialog } from '../../../components';

import {
  ProfileShortAbout,
  ProfileInstantMatchButtons,
  ProfileBackButton,
} from './';

import styles from './ProfileHeroSection.module.scss';

import { type IRatingReturn } from '../../../modules/profile/shared/pageProps/getRatingProps/@interfaces';
import { IGetSummaryReturn } from '../../../modules/profile/shared/pageProps/getSummaryProp/@interfaces';
import { IUserTransformationReturn } from '../../../modules/profile/shared/transformations/user/@interfaces';

type THeroSectionData = {
  requestId?: string;
  user: IUserTransformationReturn;
  mainSection: {
    breadcrumbs: {
      items: {
        name: string;
        url: string;
      }[];
      structuredData: string;
    };
    title: string;
  };
  resume: IGetSummaryReturn;
  rating: IRatingReturn;
  isSentMessage: boolean;
  requestUuid: string;
  slug?: string;
};

interface IProfileHeroSectionProps {
  heroSectionData: THeroSectionData;
  isInstantMatch?: boolean;
  isUserCliAuthenticated?: boolean;
  phoneNumber?: string;
}

export const ProfileHeroSection = ({
  heroSectionData,
  isInstantMatch,
  isUserCliAuthenticated = false,
  phoneNumber,
}: IProfileHeroSectionProps) => {
  const {
    isSentMessage,
    mainSection,
    rating,
    user,
    requestId,
    resume,
    requestUuid,
    slug,
  } = heroSectionData;

  const [isSentMessageSuccess, setIsSentMessageSuccess] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);

  const messageDialogText = {
    messageDescription:
      'Use esse espaço para adicionar o corpo do texto do seu modal e preenchê-lo com as informações mais essenciais.',
    successTitle: 'Mensagem enviada!',
    successDescription: `Assim que ${user.name} visualizar a sua mensagem, ele poderá te responder através do nosso chat. No caso de o profissional não estar disponível no momento, notificaremos por este canal.`,
  };

  const profileInstantMatchButtonsData = {
    user,
    requestId,
    isUserCliAuthenticated,
    isSentMessage,
    slug,
  };

  function onSendMessage(isSentMessage: boolean) {
    setIsSentMessageSuccess(isSentMessage);
  }

  function handleOpenMessageDialog() {
    setIsMessageDialogOpen(
      (prevIsMessageDialogOpen) => !prevIsMessageDialogOpen
    );
  }

  return (
    <>
      <Dialog.Root>
        <Container>
          <Row>
            <Col className={`col-sm-12 ${styles['hero-section_top-buttons']}`}>
              {shouldRenderComponent(
                [isInstantMatch, isUserCliAuthenticated],
                () => (
                  <div className="space-bottom">
                    <ProfileBackButton />
                  </div>
                )
              )}
            </Col>
          </Row>
        </Container>
      </Dialog.Root>

      <Dialog.Root
        onOpenChange={handleOpenMessageDialog}
        open={isMessageDialogOpen}
      >
        <Container>
          <Row className="bottom-sm">
            <Col className="col-sm-12 col-md-7 col-lg-6">
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: mainSection.breadcrumbs.structuredData,
                }}
              />
              <Row>
                <Col className="col-sm-12">
                  <Avatar
                    src={user.avatar}
                    alt="Imagem do profissional"
                    size="96"
                    className={styles['profile-avatar']}
                    fallback={{ name: user.name, variant: 'byName' }}
                  />
                </Col>

                <Col className={`${styles['profile-hero__title']} col-sm-12`}>
                  {user.verifiedProfile ||
                  user.partner?.documentationApproved ? (
                    user.partner?.isPartnerProfile ? (
                      <>
                        <Typography.Subtitle
                          as="h2"
                          className={`${styles['profile-name']} ${styles['verified-partner-profile']}`}
                        >
                          {user.name}
                        </Typography.Subtitle>
                        <Typography.Text>{user.description}</Typography.Text>
                      </>
                    ) : (
                      <>
                        <Typography.Subtitle
                          as="h2"
                          className={`${styles['profile-name']} ${styles['verified-profile']}`}
                        >
                          {user.name}
                        </Typography.Subtitle>
                        <Typography.Text>{user.description}</Typography.Text>
                      </>
                    )
                  ) : (
                    <Typography.Subtitle
                      as="h2"
                      className={`${styles['profile-name']}`}
                    >
                      {user.name}
                    </Typography.Subtitle>
                  )}
                </Col>
              </Row>

              <Row className={`no-margins`}>
                <Col className={`col-sm-12 ${styles['rating-section']}`}>
                  {shouldRenderComponent([!!rating], () => (
                    <section
                      className={`${styles['rating-about']} center-items`}
                      id="rating-about"
                    >
                      <Stars
                        size="small"
                        rating={rating.total}
                        quantity={rating.quantity}
                        showText={true}
                      />
                      <Typography.Text
                        className={`${styles['rating-text']} no-margins`}
                      >{`(${rating.quantity} avaliações)`}</Typography.Text>
                    </section>
                  ))}
                </Col>
              </Row>

              <Row>
                <Col className={`col-12 col-sm-12 col-md-12`}>
                  <ProfileShortAbout
                    user={user}
                    resume={resume}
                    isInstantMatch={isInstantMatch}
                    partnerProfile={false}
                  />
                </Col>
              </Row>

              <Row className={`profile-hero__service no-margins`}>
                {user.partner &&
                  shouldRenderComponent(
                    [
                      isInstantMatch,
                      INSTANT_MATCH_V1_IS_ENABLED,
                      !!user.partner.price,
                    ],
                    () => (
                      <>
                        <Col className="col-sm-12 no-margins">
                          <Row>
                            <Col
                              className={`col-sm-12 col-md-12 price ${styles['price']}`}
                            >
                              <Typography.Text
                                className={`value ${styles['value']}`}
                              >
                                A partir de:
                              </Typography.Text>
                              <Typography.Subtitle
                                size="l"
                                className={`value ${styles['value']}`}
                              >
                                {user.partner.price}
                              </Typography.Subtitle>
                            </Col>
                          </Row>
                        </Col>
                      </>
                    )
                  )}
              </Row>

              {shouldRenderComponent([isInstantMatch], () => (
                <ProfileInstantMatchButtons
                  profileInstantMatchButtonsData={
                    profileInstantMatchButtonsData
                  }
                  isSentMessageSuccess={isSentMessageSuccess}
                  slug={slug}
                />
              ))}
            </Col>
          </Row>
        </Container>

        <CIMessageDialog
          user={user}
          avatar={user.avatar}
          name={user.name}
          category={user.description}
          onSendMessage={onSendMessage}
          isSentMessageSuccess={isSentMessageSuccess}
          isMessageDialogOpen={isMessageDialogOpen}
          requestUuid={requestUuid}
          messageDescription={messageDialogText.messageDescription}
          successTitle={messageDialogText.successTitle}
          successDescription={messageDialogText.successDescription}
          phoneNumber={phoneNumber}
        />
      </Dialog.Root>
    </>
  );
};
